import React from 'react'
import {
  ButtonVariant,
  MaxWidthConstraint,
  OvalButton,
  PhotoHero,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import { GetServerSideProps } from 'next'
import Head from 'next/head'

import { API } from '../revelry_api'
import { FuneralHome, LightDarkVariant, Quote } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import { QuoteCarousel } from '../components/QuoteCarousel'
import * as Showcase from '../components/Showcase'
import { Testimonials } from '../components/Testimonials'

const quotes: Quote[] = [
  {
    body:
      "Without you in my arms, I feel an emptiness in my soul. I find myself searching the crowds for your face — I know it's an impossibility, but I cannot help myself.",
    author: 'Nicholas Sparks',
  },
  {
    body:
      'Memory is a way of holding on to the things you love, the things you are, the things you never want to lose.',
    author: 'from The Wonder Years',
  },
  { body: 'To live in hearts we leave behind is not to die.', author: 'Thomas Campbell' },
  {
    body:
      'A great soul serves everyone all the time. A great soul never dies. It brings us together again and again.',
    author: 'Maya Angelou',
  },
  {
    body: "So it's true, when all is said and done, grief is the price we pay for love.",
    author: 'E.A. Bucchianeri',
  },
  {
    body:
      "If tears could build a stairway, and memories a lane, I'd walk right up to Heaven and bring you home again.",
    author: 'Author unknown',
  },
  { body: 'Blessed are those who mourn, for they will be comforted.', author: 'Matthew 5:4' },
  { body: 'We get no choice. If we love, we grieve.', author: 'Thomas Lynch' },
  {
    body:
      'What we once enjoyed and deeply loved we can never lose, for all that we love deeply becomes part of us.',
    author: 'Helen Keller',
  },
  {
    body:
      "If there ever comes a day when we can't be together, keep me in your heart, I'll stay there forever.",
    author: 'Winnie the Pooh',
  },
  {
    body:
      'Only people who are capable of loving strongly can also suffer great sorrow, but this same necessity of loving serves to counteract their grief and heals them.',
    author: 'Leo Tolstoy',
  },
  {
    body: "In my dream, I was drowning my sorrows, but my sorrows they'd learned to swim.",
    author: 'U2 - Until The End Of The World',
  },
  {
    body: "Say not in grief 'he is no more' but live in thankfulness that he was.",
    author: 'Hebrew proverb',
  },
  {
    body:
      'Grief is not a disorder, a disease or a sign of weakness. It is an emotional, physical and spiritual necessity, the price you pay for love. The only cure for grief is to grieve.',
    author: 'Earl Grollman',
  },
  {
    body: 'Do you not know that a man is not dead while his name is still spoken?',
    author: 'Terry Pratchett',
  },
  {
    body: "Walk on, walk on with hope in your heart and you'll never walk alone.",
    author: 'Rodgers and Hammerstein, Carousel',
  },
  {
    body:
      "It's so much darker when a light goes out than it would have been if it had never shone.",
    author: 'John Steinbeck',
  },
  { body: 'Grief changes shape, but it never ends.', author: 'Keanu Reeves' },
  {
    body: "Given a choice between grief and nothing, I'd choose grief.",
    author: 'William Faulkner',
  },
  { body: 'We need never be afraid of our tears.', author: 'Charles Dickens' },
]

interface Props {
  funeralHome: FuneralHome | null
}

const YourGriefJourney: React.FC<Props> = ({ funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          Your Grief Journey - {funeralHome.name} - {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`${funeralHome.name} has many grief resources to help you navigate the loss of someone special.`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>Your Grief Journey</PhotoHero>

        <div>
          <main>
            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint className="centered-text" maxWidth={975}>
                <h2 className="h2 centered-text">
                  Grief is a journey that doesn&apos;t come with GPS to let you know where you are
                  and where you should be heading.
                </h2>

                <Spacer vertical="sm">
                  <h5 className="h5 centered-text">
                    As for grief, we find it comes in waves. The waves never stop coming, and
                    somehow we don&apos;t really want them to, but we learn we will survive them,
                    and restored is the hope for peace and healing.
                  </h5>
                </Spacer>

                <h5 className="h5 centered-text">And that, is your Grief Journey.</h5>
              </MaxWidthConstraint>
            </WorkArea>

            <Showcase.Wrapper id="holiday-program" backgroundImage="/radian.png">
              <Showcase.Primary>
                <Showcase.Video src="https://player.vimeo.com/video/647908796" />
              </Showcase.Primary>

              <Showcase.Secondary>
                <Showcase.Title>Your Grief Journey: The First Holiday Season</Showcase.Title>

                <Showcase.Text>
                  If you&apos;ve lost someone special, the holidays can be difficult. To support you
                  during this time, each year, we invite you to join us for our annual &ldquo;Your
                  Grief Journey: The First Holiday Season&rdquo; Program, where we offer a free,
                  one-hour program of practical and spiritual advice for those of us who are
                  grieving. We hope you can join us this year!
                </Showcase.Text>

                <OvalButton
                  as="anchor"
                  variant={ButtonVariant.DEFAULT_DARK}
                  target="_blank"
                  rel="noopener"
                  href="/2021-grief-seminar-handout.pdf"
                >
                  Your Grief Journey Handouts
                </OvalButton>
              </Showcase.Secondary>
            </Showcase.Wrapper>

            <WorkArea>
              <MaxWidthConstraint>
                <QuoteCarousel quotes={quotes} variant={LightDarkVariant.DARK} />
              </MaxWidthConstraint>
            </WorkArea>

            <Testimonials />
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default YourGriefJourney
